import Card from '@/components/elements/Card/Card';
import Icon from '@/components/icons/Icon';
import { Lightbox } from '@/components/modules/lightbox';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { useEffect, useRef, useState } from 'react';
import { classnames } from '@/helpers';

const baseTranslationKey = 'components.modules.salon.InstagramFeed';

type InstagramMedia = {
  id?: string;
  caption?: string;
  media_type?: string;
  media_url?: string;
  thumbnail_url?: string;
  permalink?: string;
};

type Props = {
  profile: string;
  media: InstagramMedia[];
  withInstagramIcon: boolean;
  withCard?: boolean;
};

const InstagramFeed = (props: Props) => {
  const { profile, media, withCard, withInstagramIcon } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [visibleSlides, setVisibleSlides] = useState(3);
  const carouselRef = useRef(null);
  const { isMobileView } = useMobileView();

  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleResize = () => {
    setTimeout(() => {
      if (!carouselRef.current) return;

      const carouselWidth = carouselRef.current.offsetWidth;

      setVisibleSlides(carouselWidth >= 460 ? 3 : carouselWidth >= 320 ? 2 : 1);
      setCurrentIndex(0); // Reset index on resize to prevent incorrect offsets
      setLightboxIndex(0);
    }, 250);
  };

  const lightboxPrevious = () => {
    setLightboxIndex((prevIndex) => (prevIndex - 1 + media.length) % media.length);
    gotoPrevious();
  };

  const lightboxNext = () => {
    setLightboxIndex((prevIndex) => {
      const maxIndex = media.length - 1;
      return prevIndex < maxIndex ? prevIndex + 1 : prevIndex;
    });
    gotoNext();
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    const maxIndex = media.length - visibleSlides;
    setCurrentIndex(index < maxIndex ? index : maxIndex);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  const gotoPrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const gotoNext = () => {
    setCurrentIndex((prevIndex) => {
      const maxIndex = media.length - visibleSlides;
      return prevIndex < maxIndex ? prevIndex + 1 : prevIndex;
    });
  };

  const fallbackLinkText = (
    <>
      <Icon variant="instagram" className="inline-block h-6" alt="instagram" color="white" />
      <span className="ml-1">View on Instagram</span>
    </>
  );

  // do not render if no profile or no media
  if (!profile || !media?.length) {
    return null;
  }

  const content = (
    <div
      className={`flex flex-col ${withCard ? 'gap-xl' : ''} ${withCard && !isMobileView ? 'pt-xs px-xs pb-md' : ''}`}>
      <h2
        className={classnames('text-fg_primary gap-xs flex w-full items-center text-start', {
          'px-xs': (withCard && !isMobileView) || withInstagramIcon,
          'mb-4': true,
        })}>
        {withInstagramIcon && <Icon variant="instagram" className="h-6" alt="instagram" color="fg_primary" />}
        <span className="text-h-m flex-1 font-bold">Instagram</span>
        <a
          className="text-l truncate"
          href={`https://www.instagram.com/${profile}`}
          target="_blank"
          rel="noopener noreferrer">
          @{profile}
        </a>
      </h2>

      <div className={`relative overflow-hidden`}>
        {/* Left Arrow */}
        {media.length > visibleSlides && (
          <button
            onClick={gotoPrevious}
            aria-label={_t(`${baseTranslationKey}.ariaLabel.prev`)}
            className="bg-bg_secondary_bold absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full shadow-md focus:outline-none">
            <Icon variant="chevron-left" />
          </button>
        )}

        {/* Media Carousel */}
        <div
          ref={carouselRef}
          className="-mr-2 flex transition-transform duration-500 ease-in-out"
          style={{
            transform: `translateX(-${currentIndex * (100 / visibleSlides)}%)`,
          }}>
          {media.map((item, index) => {
            return (
              <div
                key={item.id}
                onClick={() => openLightbox(index)}
                className="mr-2 flex aspect-[4/5] w-full flex-shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-md bg-[#999] bg-cover bg-center bg-no-repeat focus:outline-none"
                style={{
                  width: `calc(${100 / visibleSlides}% - 8px)`,
                  aspectRatio: '1 / 1',
                }}>
                {item.media_type === 'IMAGE' || item.media_type === 'CAROUSEL_ALBUM' ? (
                  <img
                    loading="lazy"
                    src={item.media_url}
                    alt={item.caption || 'Instagram Image'}
                    className="h-full w-full object-cover"
                  />
                ) : item.media_type === 'VIDEO' ? (
                  <video
                    poster={item.thumbnail_url}
                    className="h-full w-full object-cover"
                    onClick={(e) => e.preventDefault()}>
                    <source src={item.media_url} />
                  </video>
                ) : null}
              </div>
            );
          })}
        </div>

        {/* Right Arrow */}
        {media.length > visibleSlides && (
          <button
            onClick={gotoNext}
            aria-label={_t(`${baseTranslationKey}.ariaLabel.next`)}
            className="bg-bg_secondary_bold absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full shadow-md focus:outline-none">
            <Icon variant="chevron-right" />
          </button>
        )}
      </div>
      {/* Lightbox for larger image view */}
      {isLightboxOpen && (
        <Lightbox
          images={media.map((item) => ({
            src: item.media_url,
            caption: item.caption,
            type: item.media_type === 'VIDEO' ? 'video' : 'image',
          }))}
          currentImage={lightboxIndex}
          onClose={closeLightbox}
          onClickNext={lightboxNext}
          onClickPrev={lightboxPrevious}
          isOpen={isLightboxOpen}
          backdropClosesModal={true}
          fallbackLinkText={fallbackLinkText}
        />
      )}
    </div>
  );

  return withCard ? <Card size={'lg'}>{content}</Card> : content;
};

export default InstagramFeed;
